import { Component, ErrorInfo, ReactNode } from 'react';
import { RefreshCcw, AlertTriangle } from 'lucide-react';

interface Props {
    children: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null
    };

    public static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error("=== DETAILED ERROR REPORT ===");
        console.error("Error Message:", error.message);
        console.error("Error Name:", error.name);
        console.error("Error Stack:", error.stack);
        console.error("Component Stack:", errorInfo.componentStack);
        console.error("Full Error Object:", error);
        console.error("Full ErrorInfo:", errorInfo);

        // Log all loaded scripts
        console.error("=== LOADED SCRIPTS ===");
        const scripts = Array.from(document.querySelectorAll('script[src]'));
        scripts.forEach(script => {
            console.error(`Script: ${(script as HTMLScriptElement).src}`);
        });

        // Log build version
        console.error("=== BUILD INFO ===");
        console.error("User Agent:", navigator.userAgent);
        console.error("Timestamp:", new Date().toISOString());

        console.error("=== END ERROR REPORT ===");

        // Optional: Send to external logging service for production
        // fetch('/api/log-error', {
        //     method: 'POST',
        //     body: JSON.stringify({ error: error.message, stack: error.stack, componentStack: errorInfo.componentStack })
        // });
    }

    public render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
                    <div className="bg-white p-8 rounded-2xl shadow-xl max-w-lg w-full text-center">
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <AlertTriangle className="w-8 h-8 text-red-600" />
                        </div>
                        <h1 className="text-2xl font-bold text-slate-800 mb-2">Algo salió mal</h1>
                        <p className="text-slate-500 mb-6">
                            La aplicación ha encontrado un error inesperado. Por favor intenta recargar la página.
                        </p>

                        <div className="bg-slate-100 p-4 rounded-lg text-left text-xs font-mono text-slate-600 mb-6 overflow-auto max-h-48 border border-slate-200">
                            <div className="font-bold text-red-600 mb-2">Error: {this.state.error?.name}</div>
                            <div className="mb-3">{this.state.error?.message || 'Error desconocido'}</div>

                            {this.state.error?.stack && (
                                <details className="mt-2">
                                    <summary className="cursor-pointer font-bold text-slate-700 hover:text-slate-900">
                                        Ver Stack Trace Completo
                                    </summary>
                                    <pre className="mt-2 text-[10px] whitespace-pre-wrap break-all">
                                        {this.state.error.stack}
                                    </pre>
                                </details>
                            )}

                            {this.state.error?.message?.includes('is not a function') && (
                                <div className="mt-3 p-2 bg-amber-100 border border-amber-300 rounded text-amber-800">
                                    <strong>⚠️ Error de función detectado</strong>
                                    <br />
                                    Este error suele indicar un problema de compatibilidad de librerías.
                                    Por favor, revisa la consola del navegador para más detalles.
                                </div>
                            )}
                        </div>

                        <button
                            onClick={() => window.location.reload()}
                            className="w-full py-3 bg-primary-600 text-white rounded-lg font-bold hover:bg-primary-700 transition-colors flex items-center justify-center gap-2"
                        >
                            <RefreshCcw className="w-4 h-4" />
                            Recargar Página
                        </button>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}
